import type { BadgeType } from 'src/components/info/Badge.vue';
import { globalStore, type GlobalStore } from 'src/stores/global-store';
import type { FilterApplyValue, FilterApplyDimensions } from 'src/utilities/table-filter-telemetry-utils';

/**
 * Single dropdown option in a dropdown filter, with optional children
 */
export type DropdownOption = {
	label: string;
	value: string;
	selectable?: boolean; // Whether this option can be selected (if option has children, selects all children as well)
	icon?: string;
	entryIds?: string[];
	dotColor?: string; // If provided, shows a colored dot next to the option with the specified color
	badgeColor?: BadgeType; // If provided, shows the option as a badge with the specified background color
	entryStatusDotColor?: string; // If provided, renders a colored dot + raw label; takes precedence over badgeColor
	children?: DropdownOption[]; // Child DropdownOptions for nested dropdowns (e.g. folder DropdownOptions have form DropdownOptions as children)
	canCollapse?: boolean; // Whether this option can be expanded/collapsed if it has children
	accountMask?: string; // Used to style Destination dropdown account bullets
	excludeFromSearch?: boolean; // If true, this option's label will not be matched during search (its children can still match)
};

/**
 * Helper function to create a DropdownOption. If selectable or canCollapse are not provided, they are defaulted to true
 */
export const createDropdownOption = (params: DropdownOption) => ({
	label: params.label,
	value: params.value,
	selectable: params.selectable ?? true,
	icon: params.icon,
	entryIds: params.entryIds,
	dotColor: params.dotColor,
	badgeColor: params.badgeColor,
	entryStatusDotColor: params.entryStatusDotColor,
	children: params.children,
	canCollapse: params.canCollapse ?? true,
	accountMask: params.accountMask
});

export const createParentDropdownOption = (params: DropdownOption & { children: DropdownOption[] }) => ({
	label: params.label,
	value: params.value,
	selectable: params.selectable ?? true,
	icon: params.icon,
	dotColor: params.dotColor,
	badgeColor: params.badgeColor,
	entryStatusDotColor: params.entryStatusDotColor,
	children: params.children,
	canCollapse: params.canCollapse ?? true,
	excludeFromSearch: params.excludeFromSearch
});

/**
 * Recursively searches the options tree to find the option with the provided value
 */
export const findDropdownOption = (options: DropdownOption[], value: string | null | undefined): DropdownOption | null => {
	if (!value)
		return null;

	for (const option of options) {
		if (option.value === value)
			return option;
		if (option.children?.length) {
			const found = findDropdownOption(option.children, value);
			if (found)
				return found;
		}
	}

	return null;
};

/**
 * Resolves the dropdown option value implied by a set of applied selected options:
 * a single selection resolves to itself, multiple selections resolve to the parent
 * option whose leaf descendants exactly match the selection
 */
export const resolveSelectedOptionsValue = (options: DropdownOption[], selectedOptions: string[] | null | undefined): string | null => {
	if (!selectedOptions?.length)
		return null;
	if (selectedOptions.length === 1)
		return selectedOptions[0];

	const selected = new Set(selectedOptions);
	const getLeafValues = (option: DropdownOption): string[] =>
		option.children?.length ? option.children.flatMap(getLeafValues) : [option.value];
	const findMatchingParent = (candidates: DropdownOption[]): string | null => {
		for (const option of candidates) {
			if (!option.children?.length)
				continue;
			const leaves = getLeafValues(option);
			if (leaves.length === selected.size && leaves.every(value => selected.has(value)))
				return option.value;
			const match = findMatchingParent(option.children);
			if (match)
				return match;
		}
		return null;
	};
	return findMatchingParent(options);
};

// Internal types for filter configuration
// "key" properties refer to the keys in FilterCriteria that will be set when the filter values change
// If a specific keys are provided (eg. exactKey for amount filter), those keys will be used in FilterCriteria, and the generic "key" will be used for Vue key bindings

type DateRangeFilterParams<TFilter> = {
	key: string;
	label: string;
	startKey: keyof TFilter & string;
	endKey: keyof TFilter & string;
	minDate?: Date;
	maxDate?: Date;
};

type DropdownFilterCommonParams = {
	label: string;
	options: DropdownOption[];
	icon?: string;
	searchable?: boolean;
	searchPlaceholder?: string;
	excludeValuesFromSearch?: string[];
	showValueAsSubtext?: boolean;
	showAvatarInOptions?: boolean;
	useDotsAndBadges?: boolean; // Whether to use dotColor and badgeColor for option styling
	loadAvatars?: (entryIds: string[]) => Promise<void>;
	resolveAvatarUrl?: (entryId: string) => string | undefined;
};

/**
 * Used for Dropdown filter with "key" assigned for filter criteria properties that store a single string value (e.g. Customer)
 */
type DropdownFilterParams<TFilter> = DropdownFilterCommonParams & { key: keyof TFilter & string; selectedOptionsKey?: keyof TFilter & string };

/**
 * Used for Dropdown filter with "selectedOptionsKey" assigned for filter criteria properties that store an array of string values (e.g. FormIds).
 * "selectedValueKey" optionally names a client-only filter model key that preserves the dropdown option value the user actually
 * selected (e.g. a folder vs its single form). It is not part of the server filter criteria.
 */
type MultiSelectDropdownFilterParams<TFilter> = DropdownFilterCommonParams & { key?: keyof TFilter & string; selectedOptionsKey: keyof TFilter & string; selectedValueKey?: string; allCollapsedByDefault?: boolean };

type AmountFilterParams<TFilter> = {
	key: string;
	columnKey?: string;
	label: string;
	exactKey: keyof TFilter & string;
	minKey: keyof TFilter & string;
	maxKey: keyof TFilter & string;
	currency?: string;
	currencySymbol?: string;
	icon?: string;
	min?: number;
	max?: number;
	step?: number;
	groupKey?: string;
};

type CurrencyFilterParams<TFilter> = {
	label: string;
	icon?: string;
	modeKey?: keyof TFilter & string;
	currencyKey: keyof TFilter & string;
	originalCurrencies?: string[];
	convertedCurrencies?: string[];
	currencies?: string[];
	originalActionLabel?: string;
	convertedActionLabel?: string;
	originalCurrencyPlaceholder?: string;
	convertedCurrencyPlaceholder?: string;
	originalSelectedLabelPrefix?: string;
	convertedSelectedLabelPrefix?: string;
	groupKey?: string;
};

// Exported filter config types
export type DateRangeFilterConfig<TFilter = Record<string, unknown>> = DateRangeFilterParams<TFilter> & { type: 'date-range'; };

export type DropdownFilterConfig<TFilter = Record<string, unknown>> = (DropdownFilterParams<TFilter> | MultiSelectDropdownFilterParams<TFilter>) & { type: 'dropdown'; };

export type AmountFilterConfig<TFilter = Record<string, unknown>> = AmountFilterParams<TFilter> & { type: 'amount'; };

export type CurrencyFilterConfig<TFilter = Record<string, unknown>> = CurrencyFilterParams<TFilter> & { type: 'currency'; };

export type FilterConfig<TFilter = Record<string, unknown>> = (DateRangeFilterConfig<TFilter> | DropdownFilterConfig<TFilter> | AmountFilterConfig<TFilter> | CurrencyFilterConfig<TFilter>) & {
	columnKey?: string; dataAttrs?: Record<string, string>; applyValue?: FilterApplyValue<TFilter>; applyDimensions?: FilterApplyDimensions<TFilter>
};

// Helper functions to create filter configs
export const createDateRangeFilter = <TFilter>(params: DateRangeFilterParams<TFilter>): DateRangeFilterConfig<TFilter> => ({
	type: 'date-range',
	key: params.key,
	label: params.label,
	startKey: params.startKey,
	endKey: params.endKey,
	minDate: params.minDate,
	maxDate: params.maxDate
});

export const createDropdownFilter = <TFilter>(params: DropdownFilterParams<TFilter>): DropdownFilterConfig<TFilter> => {
	return {
		type: 'dropdown',
		key: params.key,
		selectedOptionsKey: params.selectedOptionsKey,
		label: params.label,
		icon: params.icon,
		options: params.options,
		searchable: params.searchable,
		searchPlaceholder: params.searchPlaceholder,
		excludeValuesFromSearch: params.excludeValuesFromSearch,
		showValueAsSubtext: params.showValueAsSubtext,
		showAvatarInOptions: params.showAvatarInOptions,
		useDotsAndBadges: params.useDotsAndBadges,
		loadAvatars: params.loadAvatars,
		resolveAvatarUrl: params.resolveAvatarUrl
	};
};

export const createMultiSelectDropdownFilter = <TFilter>(params: MultiSelectDropdownFilterParams<TFilter>): DropdownFilterConfig<TFilter> => {
	return {
		type: 'dropdown',
		key: params.key,
		selectedOptionsKey: params.selectedOptionsKey,
		selectedValueKey: params.selectedValueKey,
		label: params.label,
		icon: params.icon,
		options: params.options,
		searchable: params.searchable,
		searchPlaceholder: params.searchPlaceholder,
		excludeValuesFromSearch: params.excludeValuesFromSearch,
		showValueAsSubtext: params.showValueAsSubtext,
		showAvatarInOptions: params.showAvatarInOptions,
		useDotsAndBadges: params.useDotsAndBadges,
		loadAvatars: params.loadAvatars,
		resolveAvatarUrl: params.resolveAvatarUrl,
		allCollapsedByDefault: params.allCollapsedByDefault
	};
};

export const createAmountFilter = <TFilter>(params: AmountFilterParams<TFilter>): AmountFilterConfig<TFilter> & { columnKey?: string } => ({
	type: 'amount',
	key: params.key,
	columnKey: params.columnKey,
	label: params.label,
	icon: params.icon,
	groupKey: params.groupKey,
	exactKey: params.exactKey,
	minKey: params.minKey,
	maxKey: params.maxKey,
	currency: params.currency,
	currencySymbol: params.currencySymbol,
	min: params.min,
	max: params.max,
	step: params.step
});

export const createCurrencyFilter = <TFilter>(params: CurrencyFilterParams<TFilter>): CurrencyFilterConfig<TFilter> => ({
	type: 'currency',
	label: params.label,
	icon: params.icon,
	groupKey: params.groupKey,
	modeKey: params.modeKey,
	currencyKey: params.currencyKey,
	currencies: params.currencies,
	originalCurrencies: params.originalCurrencies ?? [],
	convertedCurrencies: params.convertedCurrencies ?? [],
	originalActionLabel: params.originalActionLabel,
	convertedActionLabel: params.convertedActionLabel,
	originalCurrencyPlaceholder: params.originalCurrencyPlaceholder,
	convertedCurrencyPlaceholder: params.convertedCurrencyPlaceholder,
	originalSelectedLabelPrefix: params.originalSelectedLabelPrefix,
	convertedSelectedLabelPrefix: params.convertedSelectedLabelPrefix
});

export const getDropdownSelectedValueKey = (filterConfig: DropdownFilterConfig<any>): string | undefined => {
	return 'selectedValueKey' in filterConfig ? filterConfig.selectedValueKey : undefined;
};

export const getFilterKey = (filterConfig: FilterConfig<any>): string => {
	if (filterConfig.type === 'currency')
		return String(filterConfig.currencyKey);

	return String(filterConfig.key ?? (filterConfig.type === 'dropdown' ? filterConfig.selectedOptionsKey : ''));
};

/**
 * Generates dropdown options for forms. Each form is categorized under its folder (if it has one), or archived/deleted sections as applicable.
 * Forms without folders are listed at the top level. Archived forms are further categorized into folders if they belong to an archived folder
 * @param forms list of forms (id and name) to include in the dropdown options
 * @param globalStore globalStore
 * @returns list of DropdownOptions for forms and folders, organized by folder and archived/deleted status
 */
export async function getFormsAndFoldersDropdownOptions(forms: { Id: string; Name: string }[], globalStore: GlobalStore): Promise<DropdownOption[]> {
	const folderOptionsMap: Record<string, DropdownOption & { children: DropdownOption[] }> = {};
	const archivedFolderOptionsMap: Record<string, DropdownOption & { children: DropdownOption[] }> = {};
	const formWithoutFolderOptions: DropdownOption[] = [];
	const deletedItems = createParentDropdownOption({ label: 'Deleted Items', value: 'deletedItems', children: [], selectable: false });
	const archivedItems = createParentDropdownOption({ label: 'Archived Items', value: 'archivedItems', children: [], selectable: false });

	if (!globalStore)
		return [];

	await globalStore.loadArchivedFormsIfNeeded();

	forms.forEach(({ Id: formId, Name: formName }) => {
		const form = globalStore.allForms[formId] ?? globalStore.forms[formId];
		const displayName = form?.Name ?? formName;

		if (!form) {
			// Deleted form
			deletedItems.children.push(createDropdownOption({ label: displayName, value: formId, icon: 'form' }));
		}
		else if (form.IsArchived) {
			// Archived form
			if (form.FolderId) {
				if (!archivedFolderOptionsMap[form.FolderId]) {
					const folder = globalStore.allFolders[form.FolderId] ?? globalStore.folders[form.FolderId];
					if (folder)
						archivedFolderOptionsMap[folder.Id] = createParentDropdownOption({ label: folder.Name, value: 'archived_' + folder.Id, icon: 'folder', children: [createDropdownOption({ label: displayName, value: formId, icon: 'form' })] });
				}
				else
					archivedFolderOptionsMap[form.FolderId].children.push(createDropdownOption({ label: displayName, value: formId, icon: 'form' }));
			}
			else {
				archivedItems.children.push(createDropdownOption({ label: displayName, value: formId, icon: 'form' }));
			}
		}
		else if (form.FolderId) {
			// Form in a folder
			if (!folderOptionsMap[form.FolderId]) {
				const folder = globalStore.allFolders[form.FolderId] ?? globalStore.folders[form.FolderId];
				if (folder)
					folderOptionsMap[folder.Id] = createParentDropdownOption({ label: folder.Name, value: folder.Id, icon: 'folder', children: [createDropdownOption({ label: displayName, value: formId, icon: 'form' })] });
			}
			else
				folderOptionsMap[form.FolderId].children.push(createDropdownOption({ label: displayName, value: formId, icon: 'form' }));
		}
		else {
			// Form without a folder
			formWithoutFolderOptions.push(createDropdownOption({ label: displayName, value: formId, icon: 'form' }));
		}
	});

	// Helper function to sort alphabetically
	const sortAlphabetically = (a: DropdownOption, b: DropdownOption) =>
		a.label.localeCompare(b.label);

	// Helper function to sort children of an option
	const sortChildren = (option: DropdownOption & { children?: DropdownOption[] }): DropdownOption => {
		if (option.children && option.children.length > 0) {
			return {
				...option,
				children: [...option.children].sort(sortAlphabetically)
			};
		}
		return option;
	};

	// Get folders and forms, sort them
	const folders = Object.values(folderOptionsMap).map(sortChildren);
	folders.sort(sortAlphabetically);

	formWithoutFolderOptions.sort(sortAlphabetically);

	// Build options array: folders first, then forms
	const options = [
		...folders,
		...formWithoutFolderOptions
	];

	// Handle archived items
	if (Object.values(archivedFolderOptionsMap).length) {
		const archivedFolders = Object.values(archivedFolderOptionsMap).map(sortChildren);
		archivedFolders.sort(sortAlphabetically);

		if (archivedItems.children.length) {
			archivedItems.children.sort(sortAlphabetically);
			archivedItems.children = [
				...archivedFolders,
				...archivedItems.children
			];
		}
		else {
			archivedItems.children = archivedFolders;
		}
	}
	else if (archivedItems.children.length) {
		archivedItems.children.sort(sortAlphabetically);
	}

	if (archivedItems.children.length)
		options.push(archivedItems);

	// Handle deleted items
	if (deletedItems.children.length) {
		deletedItems.children.sort(sortAlphabetically);
		options.push(deletedItems);
	}

	return options;
}

/**
 * Sorts customer dropdown options with "Unknown Customer" first, then customers with names (sorted alphabetically), then customers without names (sorted alphabetically)
 * @param options List of Dropdown Options for a customer dropdown
 * @param anonymousCustomerFilterValue The value used for the "Unknown Customer" filter option
 * @returns List of sorted DropdownOptions
 */
export function sortCustomerDropdownOptions(options: DropdownOption[], anonymousCustomerFilterValue: string): DropdownOption[] {
	if (!options || options.length === 0)
		return [];

	// The option for filtering by "Unknown Customer" goes first
	const anonymousCustomer = options.filter(opt => opt.value === anonymousCustomerFilterValue);

	// Next are options that have both name and email (value and label are different)
	const customersWithNames = options.filter(opt => opt.value !== anonymousCustomerFilterValue && opt.label !== opt.value);

	// Finally, options that have only email (label equals value)
	const customersWithoutNames = options.filter(opt => opt.value !== anonymousCustomerFilterValue && opt.label === opt.value);

	// Sort each group alphabetically by label
	const sortAlphabetically = (a: DropdownOption, b: DropdownOption) =>
		a.label.localeCompare(b.label, undefined, { sensitivity: 'base' });

	customersWithNames.sort(sortAlphabetically);
	customersWithoutNames.sort(sortAlphabetically);

	// Combine in the specified order
	return [...anonymousCustomer, ...customersWithNames, ...customersWithoutNames];
}

function getCurrencyToken(locale: string, currency: string, currencyDisplay: 'code' | 'narrowSymbol' | 'symbol') {
	return new Intl.NumberFormat(locale, {
		style: 'currency',
		currency,
		currencyDisplay
	}).formatToParts(0).find(part => part.type === 'currency')?.value?.trim() ?? '';
}

function isCurrencyCodeToken(token: string, currency: string) {
	return token.toUpperCase() === currency;
}

function formatCurrencyMagnitude(amount: number, currency: string, locale: string, compact = false) {
	if (compact)
		return new Intl.NumberFormat(locale, { notation: 'compact', maximumFractionDigits: 1 }).format(amount);

	const { minimumFractionDigits, maximumFractionDigits } = new Intl.NumberFormat(locale, {
		style: 'currency',
		currency,
		currencyDisplay: 'code'
	}).resolvedOptions();
	return new Intl.NumberFormat(locale, { minimumFractionDigits, maximumFractionDigits }).format(amount);
}

export function normalizeCurrency(currency?: string | null): string {
	return currency?.trim().toUpperCase() ?? '';
}

type CurrencyDisplayStrategy = {
	currencyDisplay: 'code' | 'symbol' | 'narrowSymbol';
	displayToken: string;
	appendCode: boolean;
};

// Determines how to display a currency amount based on the locale's available currency tokens for that currency.
function getCurrencyDisplayStrategy(locale: string, currency: string): CurrencyDisplayStrategy {
	const narrowToken = getCurrencyToken(locale, currency, 'narrowSymbol');
	const symbolToken = getCurrencyToken(locale, currency, 'symbol');
	const narrowSymbolIsCode = isCurrencyCodeToken(narrowToken, currency);
	const symbolIsCode = isCurrencyCodeToken(symbolToken, currency);
	const useSymbol = !narrowSymbolIsCode && !symbolIsCode && symbolToken !== narrowToken;

	return {
		currencyDisplay: narrowSymbolIsCode ? 'code' : (useSymbol ? 'symbol' : 'narrowSymbol'),
		displayToken: narrowSymbolIsCode ? currency : (useSymbol ? symbolToken : narrowToken),
		appendCode: !narrowSymbolIsCode && (symbolIsCode || symbolToken !== narrowToken)
	};
}

export type TableFilterCurrencyFormatOptions = {
	format?: 'default' | 'fx';
	absolute?: boolean;
	suppressCode?: boolean;
	organizationCurrency?: string;
	compact?: boolean;
};

/**
 * Formats a currency amount for display in tables, filters, dialogs, and charts. The organization currency
 * defaults to the current org's currency; when the amount is in that currency it renders with a symbol,
 * otherwise it renders as `{ISO code} {amount}`.
 */
export function formatTableFilterCurrency(
	amount: number,
	currency: string,
	locale: string = 'en-US',
	options: TableFilterCurrencyFormatOptions = {}
): string {
	const normalizedCurrency = normalizeCurrency(currency);
	if (!normalizedCurrency)
		return '';

	const normalizedAmount = options.absolute ? Math.abs(amount) : amount;
	const strategy = getCurrencyDisplayStrategy(locale, normalizedCurrency);
	const normalizedOrganizationCurrency = normalizeCurrency(options.organizationCurrency ?? globalStore.organization?.Currency);
	const sign = normalizedAmount < 0 ? '-' : '';
	const formattedMagnitude = formatCurrencyMagnitude(Math.abs(normalizedAmount), normalizedCurrency, locale, options.compact);
	const hasDistinctSymbol = !isCurrencyCodeToken(strategy.displayToken, normalizedCurrency);
	const symbolAmount = hasDistinctSymbol
		? `${sign}${strategy.displayToken}${formattedMagnitude}`
		: `${sign}${formattedMagnitude}`;

	if (normalizedOrganizationCurrency) {
		if (normalizedCurrency === normalizedOrganizationCurrency || options.suppressCode)
			return hasDistinctSymbol ? symbolAmount : `${normalizedCurrency} ${sign}${formattedMagnitude}`;

		return `${normalizedCurrency} ${sign}${formattedMagnitude}`;
	}

	if (options.format !== 'fx') {
		const formatted = new Intl.NumberFormat(locale, {
			style: 'currency',
			currency: normalizedCurrency,
			currencyDisplay: strategy.currencyDisplay,
			...(options.compact ? { notation: 'compact', maximumFractionDigits: 1 } : {})
		}).format(normalizedAmount);
		if (strategy.appendCode && !options.suppressCode)
			return `${formatted} ${normalizedCurrency}`;
		else
			return formatted;
	}

	const fxAmount = hasDistinctSymbol
		? `${sign}${strategy.displayToken}${formattedMagnitude}`
		: `${strategy.displayToken} ${sign}${formattedMagnitude}`;
	if (strategy.appendCode && !options.suppressCode)
		return `${fxAmount} ${normalizedCurrency}`;
	return fxAmount;
}
